import React, { useState, useEffect } from "react";
import { Card, List, Descriptions, Table, Button, Space, Modal, Alert, Collapse } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { useCustomMutation } from "@refinedev/core";
import { Sourcing, SourcingStatus } from "../interface";
import { OPEN_BIDS_MUTATION } from "../queries";

interface BidsTabProps {
  record: Sourcing;
  query: any;
  refetchBidCount: any;
  refetchBids: any;
}

export const BidsTab: React.FC<BidsTabProps> = ({ record, query, refetchBidCount, refetchBids }) => {
  // State for bid count
  const [bidCount, setBidCount] = useState<number>(0);
  const [bids, setBids] = useState<any[]>([]);
  const [bidsLoading, setBidsLoading] = useState(false);
  const [bidCountFetched, setBidCountFetched] = useState<boolean>(false);
  const [isOpenBidsModalVisible, setIsOpenBidsModalVisible] = useState(false);

  // Debug: Log bid count changes
  useEffect(() => {
    console.log('Bid count changed to:', bidCount);
  }, [bidCount]);

  // Fetch bid count when record changes - only for PUBLISHED and CLOSED
  useEffect(() => {
    if (record?.id && refetchBidCount && !bidCountFetched &&
        (record.status === SourcingStatus.PUBLISHED || record.status === SourcingStatus.CLOSED)) {
      console.log('Fetching bid count for sourcingId:', record.id);
      refetchBidCount().then((response: any) => {
        console.log('Bid count response:', response);

        // Handle different possible response structures
        let count = 0;
        if (response && (response as any).data) {
          const responseData = (response as any).data;
          if (typeof responseData === 'object' && responseData !== null) {
            // Check for direct property access
            if ('countBidsBySourcing' in responseData) {
              count = responseData.countBidsBySourcing || 0;
            }
            // Check for nested data structure
            else if (responseData.data && 'countBidsBySourcing' in responseData.data) {
              count = responseData.data.countBidsBySourcing || 0;
            }
          } else if (typeof responseData === 'number') {
            count = responseData;
          }
        }

        console.log('Setting bid count to:', count);
        setBidCount(count);
        setBidCountFetched(true); // Mark as fetched to prevent re-fetching
      }).catch((error: any) => {
        console.error('Error fetching bid count:', error);
        setBidCount(0); // Reset to 0 on error
        setBidCountFetched(true); // Mark as fetched even on error
      });
    }
  }, [record?.id, refetchBidCount, bidCountFetched]);

  // Handle get bids button click
  const handleGetBids = async () => {
    if (!record?.id || !refetchBids) return;

    console.log('Getting bids for sourcingId:', record.id);
    setBidsLoading(true);
    try {
      const response = await refetchBids();
      console.log('Get bids response:', response);

      let bidsData: any[] = [];
      if (response && (response as any).data) {
        const responseData = (response as any).data;
        if (typeof responseData === 'object' && responseData !== null) {
          // Check for direct property access
          if ('getBidsBySourcing' in responseData) {
            bidsData = responseData.getBidsBySourcing || [];
          }
          // Check for nested data structure
          else if (responseData.data && 'getBidsBySourcing' in responseData.data) {
            bidsData = responseData.data.getBidsBySourcing || [];
          }
        } else if (Array.isArray(responseData)) {
          bidsData = responseData;
        }
      }

      console.log('Setting bids to:', bidsData);
      setBids(bidsData);
    } catch (error) {
      console.error('Error fetching bids:', error);
      setBids([]); // Reset to empty array on error
    } finally {
      setBidsLoading(false);
    }
  };

  // Handle open bids confirmation
  const handleOpenBidsConfirmation = () => {
    setIsOpenBidsModalVisible(true);
  };

  // Mutation for opening bids
  const { mutate: openBids, isLoading: isOpeningBids } = useCustomMutation();

  // Handle open bids confirmation OK
  const handleOpenBidsConfirm = async () => {
    if (!record?.id) return;

    setIsOpenBidsModalVisible(false);
    setBidsLoading(true);

    try {
      // Call the openBids mutation
      openBids({
        meta: {
          gqlMutation: OPEN_BIDS_MUTATION,
        },
        values: {
          input: {
            sourcingId: record.id
          }
        },
        url: "",
        method: "post"
      }, {
        onSuccess: async () => {
          // Refetch the sourcing data to get updated publishData
          await query.refetch();

          // Also refetch bid count to update the UI
          if (refetchBidCount) {
            await refetchBidCount();
          }
        },
        onError: (error) => {
          console.error('Error opening bids:', error);
        }
      });

    } catch (error) {
      console.error('Error opening bids:', error);
    } finally {
      setBidsLoading(false);
    }
  };

  // Handle open bids confirmation cancel
  const handleOpenBidsCancel = () => {
    setIsOpenBidsModalVisible(false);
  };

  // Check if deadline has passed
  const isDeadlinePassed = record?.publishData?.dueDate
    ? new Date(record.publishData.dueDate) < new Date()
    : false;

  // Calculate time remaining until deadline
  const getTimeUntilDeadline = () => {
    if (!record?.publishData?.dueDate) return null;

    const deadline = new Date(record.publishData.dueDate);
    const now = new Date();
    const timeDiff = deadline.getTime() - now.getTime();

    if (timeDiff <= 0) return null; // Deadline has passed

    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

    let timeString = '';
    if (days > 0) {
      timeString = `${days} day${days > 1 ? 's' : ''} ${hours} hour${hours > 1 ? 's' : ''}`;
    } else if (hours > 0) {
      timeString = `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes > 1 ? 's' : ''}`;
    } else {
      timeString = `${minutes} minute${minutes > 1 ? 's' : ''}`;
    }

    return timeString;
  };



  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {/* Closed Sourcing Alert */}
      {record && record.status === SourcingStatus.CLOSED && record.openedAt && (
        <Alert
          message="Bids Opened Successfully"
          description={
            <div>
              <p><strong>Bid has been opened and is now Closed for Submission</strong></p>
              <p><strong>Ready to be In Evaluation</strong></p>
              <div style={{ marginTop: 8 }}>
                <p><strong>Opened By:</strong> {record.openedBy || 'N/A'}</p>
                <p><strong>Opened At:</strong> {new Date(record.openedAt).toLocaleString()}</p>
              </div>
            </div>
          }
          type="success"
          showIcon
          action={
            <Button
              type="primary"
              onClick={() => handleGetBids()}
            >
              View Bids
            </Button>
          }
        />
      )}

      {/* Open Bids Button */}
      {record && record.status === SourcingStatus.PUBLISHED && record.publishData && record.id && isDeadlinePassed && (
        <Card>
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <div>
              <h3>Open Bids</h3>
              <p>Click the button to open and review all submitted bids</p>
            </div>
            <Button
              type="primary"
              size="large"
              onClick={handleOpenBidsConfirmation}
              loading={bidsLoading || isOpeningBids}
            >
              Open and Get {bidCount} bids
            </Button>
          </Space>
        </Card>
      )}

      {/* Bids Display */}
      {(bids.length > 0 || (record?.publishData?.bids && record.publishData.bids.length > 0)) && record?.id && (
        <Card title="Submitted Bids">
          <List
            dataSource={record?.publishData?.bids || bids}
            renderItem={(bid: any) => {
              // Calculate total amount by summing items if not defined
              const totalAmount = bid.totalAmount || (bid.items?.reduce((sum: number, item: any) => sum + (item.total || 0), 0) || 0);
              
              return (
                <List.Item>
                  <List.Item.Meta
                    title={bid.supplier?.companyName || bid.supplierName}
                    description={
                      <Space direction="vertical" size="small">
                        <div>
                          Total Amount: {record.currency || 'ETB'} {totalAmount.toLocaleString()}
                        </div>
                        <div>
                          Submitted: {new Date(bid.submissionDate).toLocaleString()}
                        </div>
                        <div>
                          Status: {bid.status}
                        </div>
                      </Space>
                    }
                  />
                  <Collapse defaultActiveKey={[]} size="small">
                    {/* Items Section */}
                    {bid.items && bid.items.length > 0 && (
                      <Collapse.Panel header={`Items (${bid.items.length})`} key="items">
                        <Table
                          dataSource={bid.items}
                          columns={[
                            {
                              title: "Item Name",
                              dataIndex: "itemName",
                              key: "itemName",
                            },
                            {
                              title: "Description",
                              dataIndex: "description",
                              key: "description",
                            },
                            {
                              title: "Quantity",
                              dataIndex: "quantity",
                              key: "quantity",
                            },
                            {
                              title: "Unit",
                              dataIndex: "unit",
                              key: "unit",
                            },
                            {
                              title: "Unit Price",
                              dataIndex: "unitPrice",
                              key: "unitPrice",
                              render: (value: number) => `${record.currency || "ETB"} ${value?.toLocaleString()}`,
                            },
                            {
                              title: "Total",
                              dataIndex: "total",
                              key: "total",
                              render: (value: number) => `${record.currency || "ETB"} ${value?.toLocaleString()}`,
                            },
                            {
                              title: "Delivery Time",
                              dataIndex: "deliveryTime",
                              key: "deliveryTime",
                            },
                          ]}
                          rowKey="itemName"
                          pagination={false}
                          size="small"
                        />
                      </Collapse.Panel>
                    )}
                    
                    {/* Fields Section */}
                    {bid.fields && bid.fields.length > 0 && (
                      <Collapse.Panel header={`Fields (${bid.fields.length})`} key="fields">
                        <Table
                          dataSource={bid.fields}
                          columns={[
                            {
                              title: "Field Name",
                              dataIndex: "fieldName",
                              key: "fieldName",
                            },
                            {
                              title: "Field Value",
                              dataIndex: "fieldValue",
                              key: "fieldValue",
                            },
                          ]}
                          rowKey="fieldName"
                          pagination={false}
                          size="small"
                        />
                      </Collapse.Panel>
                    )}
                    
                    {/* Documents Section */}
                    {bid.documents && bid.documents.length > 0 && (
                      <Collapse.Panel header={`Documents (${bid.documents.length})`} key="documents">
                        <List
                          dataSource={bid.documents}
                          renderItem={(doc: any) => (
                            <List.Item key={doc.id}>
                              <Space>
                                <span>{doc.documentName || doc.fileName || 'Document'}</span>
                                {doc.url && (
                                  <a href={doc.url} target="_blank" rel="noopener noreferrer">
                                    View
                                  </a>
                                )}
                              </Space>
                            </List.Item>
                          )}
                        />
                      </Collapse.Panel>
                    )}
                  </Collapse>
                </List.Item>
              );
            }}
          />
        </Card>
      )}

      {/* Published Information */}
      {record.publishData && (
        <Card title="Published Information">
          <Descriptions column={2}>
            <Descriptions.Item label="Published Title">{record.publishData.title}</Descriptions.Item>
            <Descriptions.Item label="Sourcing Type">{record.publishData.sourcingType}</Descriptions.Item>
            <Descriptions.Item label="Bidding Type">{record.publishData.biddingType}</Descriptions.Item>
            <Descriptions.Item label="Status">{record.publishData.status}</Descriptions.Item>
            <Descriptions.Item label="Public">{record.publishData.isPublic ? 'Yes' : 'No'}</Descriptions.Item>
            <Descriptions.Item label="Due Date">
              {new Date(record.publishData.dueDate).toLocaleDateString()}
            </Descriptions.Item>
            <Descriptions.Item label="Template">
              {record.publishData.template?.name} ({record.publishData.template?.id})
            </Descriptions.Item>
            <Descriptions.Item label="Invited Suppliers">
              {record.publishData.invitedSuppliers?.length} suppliers
            </Descriptions.Item>
            {record.publishData.openedAt && (
              <Descriptions.Item label="Opened Date">
                {new Date(record.publishData.openedAt).toLocaleDateString()}
              </Descriptions.Item>
            )}
            {record.publishData.openedBy && (
              <Descriptions.Item label="Opened By">
                {record.publishData.openedBy.name}
              </Descriptions.Item>
            )}
          </Descriptions>

          {record.publishData.description && (
            <div style={{ marginTop: 16 }}>
              <strong>Description:</strong>
              <p>{record.publishData.description}</p>
            </div>
          )}

          {record.publishData.invitedSuppliers && record.publishData.invitedSuppliers.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <strong>Invited Suppliers:</strong>
              <List
                dataSource={record.publishData.invitedSuppliers}
                renderItem={(supplier: any) => (
                  <List.Item>
                    <List.Item.Meta
                      title={supplier.companyName}
                      description={`ID: ${supplier.id}`}
                    />
                  </List.Item>
                )}
              />
            </div>
          )}

          {record.publishData.fields && record.publishData.fields.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <strong>Custom Fields:</strong>
              <Table
                dataSource={record.publishData.fields}
                columns={[
                  {
                    title: 'Field Name',
                    dataIndex: 'name',
                    key: 'name',
                  },
                  {
                    title: 'Label',
                    dataIndex: 'label',
                    key: 'label',
                  },
                  {
                    title: 'Type',
                    dataIndex: 'type',
                    key: 'type',
                  },
                  {
                    title: 'Required',
                    dataIndex: 'isRequired',
                    key: 'isRequired',
                    render: (value: boolean) => value ? 'Yes' : 'No',
                  },
                ]}
                rowKey="name"
                pagination={false}
                size="small"
              />
            </div>
          )}

          {record.publishData.fileRequirements && record.publishData.fileRequirements.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <strong>File Requirements:</strong>
              <Table
                dataSource={record.publishData.fileRequirements}
                columns={[
                  {
                    title: 'Name',
                    dataIndex: 'name',
                    key: 'name',
                  },
                  {
                    title: 'Label',
                    dataIndex: 'label',
                    key: 'label',
                  },
                  {
                    title: 'Document Type',
                    dataIndex: 'documentType',
                    key: 'documentType',
                  },
                  {
                    title: 'Required',
                    dataIndex: 'isRequired',
                    key: 'isRequired',
                    render: (value: boolean) => value ? 'Yes' : 'No',
                  },
                ]}
                rowKey="name"
                pagination={false}
                size="small"
              />
            </div>
          )}
        </Card>
      )}

      {/* Confirmation Modal for Opening Bids */}
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#faad14' }} />
            Confirm Bid Opening
          </Space>
        }
        open={isOpenBidsModalVisible}
        onOk={handleOpenBidsConfirm}
        onCancel={handleOpenBidsCancel}
        okText="Open Bids"
        cancelText="Cancel"
        width={800}
        destroyOnClose
      >
        <div style={{ marginBottom: 16 }}>
          <Alert
            message="Ready to Open Bids"
            description={`${bidCount} bids have been submitted and are ready for review.`}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        </div>

        <div style={{ marginTop: 16 }}>
          <p style={{ marginBottom: 8, fontWeight: 500 }}>
            By clicking "Open Bids", you confirm that:
          </p>
          <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
            <li style={{ marginBottom: 4 }}>
              The sourcing will be marked as closed
            </li>
            <li style={{ marginBottom: 4 }}>
              All individual bids will be marked as under review
            </li>
            <li style={{ marginBottom: 4 }}>
              Notifications will be sent to all bidders
            </li>
            <li style={{ marginBottom: 0 }}>
              You will be able to review and evaluate all submitted bids
            </li>
          </ul>
        </div>
      </Modal>
    </Space>
  );
};